import { FC, useMemo } from 'react';
import { Checkbox, Flex } from 'antd';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import {
  borderRadiusSM,
  colorBlue,
  colorFillTertiary,
  colorTextQuaternary,
} from '@atlas-frontend/design-tokens';
import { Padding, TextEllipsis } from '../../common/components';
import { CategoryHierarchyNode } from '../../classifier';

export interface CustomColumnedCategorySelectOptionProps {
  columns: [string | null, CategoryHierarchyNode[]][];
  value: string[];
  category: CategoryHierarchyNode;
  onClick?: (isOpen: boolean, hasChildren: boolean) => void;
  onCheckboxChange?: (checked: boolean) => void;
}

export const CustomColumnedCategorySelectOption: FC<
  CustomColumnedCategorySelectOptionProps
> = (props) => {
  const { columns, value, category, onClick, onCheckboxChange } = props;
  const { code, displayName, children } = category;
  const hasChildren = children.length > 0;

  const { isOpen, isParentOfOpen } = useMemo(() => {
    const isOpen = columns[columns.length - 1][0] === code;
    return {
      isOpen,
      isParentOfOpen: isOpen || columns.some(([c]) => c === code),
    };
  }, [columns, code]);

  const isChecked = useMemo(() => value.includes(code), [value, code]);
  const isParentOfChecked = useMemo(
    () => !isChecked && checkIsParentOfChecked(children, value),
    [isChecked, children, value]
  );

  return (
    <OptionWrapper
      vertical={5}
      horizontal={10}
      key={code}
      onClick={() => onClick?.(isOpen, hasChildren)}
      $hasChildren={hasChildren}
      $isOpen={isParentOfOpen}
    >
      <Flex gap="small" align="center" justify="space-between">
        <CheckboxAndLabelWrapper gap="small" align="center">
          <Checkbox
            id={`custom-columned-category-select-option-${code}`}
            checked={isChecked}
            onChange={({ target }) => onCheckboxChange?.(target.checked)}
            onClick={(event) => event.stopPropagation()}
            indeterminate={isParentOfChecked}
          />
          <TextEllipsis>{displayName}</TextEllipsis>
        </CheckboxAndLabelWrapper>
        {isParentOfOpen && (
          <ChevronRight size={16} color={colorTextQuaternary} />
        )}
      </Flex>
    </OptionWrapper>
  );
};

const checkIsParentOfChecked = (
  children: CategoryHierarchyNode[],
  checkedCodes: string[]
): boolean =>
  children.some(
    (child) =>
      checkedCodes.includes(child.code) ||
      checkIsParentOfChecked(child.children, checkedCodes)
  );

const ChevronRight = styled(Icon.ChevronRight)`
  flex-shrink: 0;
`;

const CheckboxAndLabelWrapper = styled(Flex)`
  min-width: 0;
`;

const OptionWrapper = styled(Padding)<{
  $hasChildren: boolean;
  $isOpen: boolean;
}>`
  border-radius: ${borderRadiusSM}px;

  ${(props) =>
    props.$hasChildren &&
    `
    cursor: pointer;

    &:hover {
      background-color: ${colorFillTertiary};
    }
  `}

  ${(props) =>
    props.$isOpen &&
    `
    font-weight: 500;
    background-color: ${colorBlue[0]};
  `}
`;
